"use client";

/**
 * Context-capture provider for the Procore side panel — the Procore analogue
 * of `OfficeProvider`, but far lighter: there is no Office.js to wait on.
 * Procore delivers the panel context (company/project/resource/view) only over
 * `postMessage`, and only after the app posts an `{ type: "initialize" }`
 * handshake to the parent window — Procore replies with a
 * `{ type: "setup", context }` message. (URL-param interpolation is NOT used
 * for context; `@procore/procore-iframe-helpers@0.2.3` is auth-only and does no
 * context handshake, so this provider performs it directly.)
 *
 * The captured context is written to the surface-keyed **host context store**
 * (`lib/host-context-store.ts`); the chat composer reads it generically at
 * send-time and the server's per-surface renderer turns it into the prompt's
 * `<procore_context>` block. This component renders children unchanged — it is
 * a side-effect provider, not a React context.
 */
import { c as _c } from "react/compiler-runtime";
import { PROCORE_SURFACE } from "@workspace/copilots";
import { type ReactNode, useEffect } from "react";
import { setHostContext } from "@/lib/host-context-store";

/** Captured Procore side-panel context (the opaque `data` for this surface). */
export type ProcorePanelContext = {
  companyId?: string;
  projectId?: string;
  resourceId?: string;
  view?: string;
};

/**
 * Procore side-panel lifecycle event, delivered over `postMessage`, fired when
 * the panel becomes visible in a view. Because the Magnus iframe is NOT
 * remounted when the user navigates between Procore views (Submittals → RFIs),
 * this is our signal to re-solicit context — otherwise the first view's context
 * stays pinned forever. See `interpretProcoreMessage` / `requestContext`.
 */
const SIDEPANEL_VISIBLE_EVENT = "sidepanel:app:visible";

/**
 * Outcome of interpreting a `postMessage` from the Procore parent:
 * - `request` — re-solicit context (panel became visible in a possibly new view)
 * - `setup` — Procore delivered fresh context to store
 * - `ignore` — not from Procore, or an unrelated/malformed message
 */
export type ProcoreMessageAction = {
  kind: "request";
} | {
  kind: "setup";
  context: ProcorePanelContext;
} | {
  kind: "ignore";
};

/**
 * Pure interpretation of an incoming postMessage — decoupled from the DOM so it
 * can be unit-tested. The provider wires this to `window`'s `message` event.
 */
export function interpretProcoreMessage(origin: string, data: unknown): ProcoreMessageAction {
  if (!isProcoreOrigin(origin)) {
    return {
      kind: "ignore"
    };
  }
  if (!data || typeof data !== "object") {
    return {
      kind: "ignore"
    };
  }
  const message = data as {
    type?: unknown;
    context?: Record<string, unknown>;
  };
  // The panel became visible in a (possibly new) view — re-request context so a
  // stale view/resource from the previous view is replaced.
  if (message.type === SIDEPANEL_VISIBLE_EVENT) {
    return {
      kind: "request"
    };
  }
  if (message.type !== "setup") {
    return {
      kind: "ignore"
    };
  }
  const ctx = message.context ?? {};
  return {
    kind: "setup",
    context: {
      companyId: asString(ctx.company_id),
      projectId: asString(ctx.project_id),
      // Procore sends the resource id as `id`, not `resource_id`.
      resourceId: asString(ctx.id),
      view: asString(ctx.view)
    }
  };
}

/**
 * Well-known Procore parent origins we always post the `initialize` handshake
 * to. postMessage requires a concrete origin (no wildcards), so we enumerate
 * the common hosts and post to each — only the true parent receives it; the
 * rest are harmless no-ops. Mirrors the CSP `frame-ancestors` allow-list.
 *
 * This is a floor, not the whole set: the true parent origin is also derived
 * from `document.referrer` at runtime (see `resolveHandshakeOrigins`) so
 * regional/login/future Procore subdomains that pass `isProcoreOrigin` still
 * receive the handshake even though they aren't enumerated here.
 */
const PROCORE_PARENT_ORIGINS = ["https://sandbox.procore.com", "https://app.procore.com", "https://procore.brasfieldgorrie.com"] as const;

/** True when an incoming message origin is a trusted Procore host. */
function isProcoreOrigin(origin: string): boolean {
  let hostname: string;
  try {
    hostname = new URL(origin).hostname;
  } catch {
    return false;
  }
  return hostname === "procore.brasfieldgorrie.com" || hostname === "procore.com" || hostname.endsWith(".procore.com");
}

/**
 * The concrete origins to post the `initialize` handshake to: the known hosts
 * plus the actual embedding parent's origin (from `document.referrer`) when it
 * passes the same `isProcoreOrigin` guard the receiver uses. Deriving the send
 * target from the receive rule keeps the two allow-lists in sync, so a panel
 * embedded under any trusted Procore host (regional, login, future subdomains)
 * still reaches its true parent.
 */
function resolveHandshakeOrigins(): string[] {
  const origins = new Set<string>(PROCORE_PARENT_ORIGINS);
  const referrer = document.referrer;
  if (referrer) {
    try {
      const referrerOrigin = new URL(referrer).origin;
      if (isProcoreOrigin(referrerOrigin)) {
        origins.add(referrerOrigin);
      }
    } catch {
      // Malformed referrer — ignore; the known hosts still cover the common case.
    }
  }
  return [...origins];
}

/** Narrow an unknown postMessage field to a non-empty string, else undefined. */
function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}
export function ProcoreProvider(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp2, t1);
  let t2;
  if ($[1] !== children) {
    t2 = <>{children}</>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp2() {
  const requestContext = _temp;
  const onMessage = event => {
    const action = interpretProcoreMessage(event.origin, event.data);
    if (action.kind === "request") {
      requestContext();
    } else {
      if (action.kind === "setup") {
        setHostContext(PROCORE_SURFACE, action.context);
      }
    }
  };
  const onVisibility = () => {
    if (document.visibilityState === "visible") {
      requestContext();
    }
  };
  window.addEventListener("message", onMessage, false);
  document.addEventListener("visibilitychange", onVisibility, false);
  window.addEventListener("focus", requestContext, false);
  requestContext();
  return () => {
    window.removeEventListener("message", onMessage, false);
    document.removeEventListener("visibilitychange", onVisibility, false);
    window.removeEventListener("focus", requestContext, false);
  };
}
function _temp() {
  for (const origin of resolveHandshakeOrigins()) {
    try {
      window.parent.postMessage({
        type: "initialize"
      }, origin);
    } catch {}
  }
}